import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { dimensionLabel, dimensionShort, rubricColor } from "../lib/scoring";
import TrendMarkButton from "../components/TrendMarkButton";

/**
 * Sprint 7 — Multi-wave trends, scoped to one organization.
 *
 * Surfaces wave-over-wave change at the dimension level for any
 * organization that has at least two waves crossing n>=5. Per-wave
 * suppression is honored: a wave below threshold renders as n<5 with
 * no scores, and is excluded from delta calculations.
 *
 * Marks: each Δ row carries a "+ Mark" affordance per dimension. A
 * marked delta lands on page 5 of the destination wave's PDF report —
 * the destination wave being the "to" side of the delta.
 *
 * Route: /dashboard/orgs/:orgId/trends
 */
const DIM_ORDER = [
  "optimism", "emotional_regulation", "ownership", "flexibility",
  "social_support", "purpose", "grit",
];

export default function OrgTrends() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [marksByDep, setMarksByDep] = useState({});   // {to_deployment_id: [mark, ...]}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: payload } = await api.get(`/api/organizations/${orgId}/trends`);
        if (cancelled) return;
        setData(payload);
        // For each "to_wave" that appears as the destination of a delta, fetch
        // its marks so we can show the marked/unmarked state on each row.
        const toDeps = new Set();
        for (const d of payload.deltas || []) {
          const dep = (payload.waves || []).find((w) => w.wave_number === d.to_wave);
          if (dep) toDeps.add(dep.deployment_id);
        }
        if (payload.headline_delta) {
          const dep = (payload.waves || []).find((w) => w.wave_number === payload.headline_delta.to_wave);
          if (dep) toDeps.add(dep.deployment_id);
        }
        const entries = await Promise.all(
          [...toDeps].map(async (depId) => {
            try {
              const { data: m } = await api.get(`/api/deployments/${depId}/marked-items`);
              return [depId, m.marks || []];
            } catch (_) {
              return [depId, []];
            }
          }),
        );
        if (!cancelled) {
          setMarksByDep(Object.fromEntries(entries));
        }
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  const usableWaves = useMemo(
    () => (data?.waves || []).filter((w) => !w.suppressed),
    [data],
  );

  const waveByNumber = useMemo(() => {
    const map = {};
    (data?.waves || []).forEach((w) => { map[w.wave_number] = w; });
    return map;
  }, [data]);

  function onMarkChange(toDepId, savedOrNull, fromDeploymentId, dimension) {
    setMarksByDep((cur) => {
      const list = (cur[toDepId] || []).filter((m) => {
        if (m.kind !== "trend_delta") return true;
        const p = m.payload || {};
        return !(p.dimension === dimension && p.from_deployment_id === fromDeploymentId);
      });
      const next = savedOrNull ? [...list, savedOrNull] : list;
      return { ...cur, [toDepId]: next };
    });
  }

  function findMark(toDepId, fromDepId, dim) {
    const marks = marksByDep[toDepId] || [];
    return marks.find((m) =>
      m.kind === "trend_delta" &&
      m.payload?.dimension === dim &&
      m.payload?.from_deployment_id === fromDepId,
    ) || null;
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="org-trends-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-quiet text-xs"
              data-testid="back-to-dashboard"
            >
              ← All deployments
            </button>
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-sm text-gray-700 hidden md:block">{user?.name}</div>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-8 py-12">
        {loading && <div className="text-sm text-gray-500" data-testid="org-trends-loading">Loading…</div>}
        {error && (
          <div className="panel mb-8" style={{ borderColor: "#AD393E" }} data-testid="org-trends-error">
            <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Couldn&rsquo;t load trends</div>
            <div className="text-sm">{error}</div>
          </div>
        )}
        {data && (
          <>
            <header className="mb-10">
              <div className="eyebrow mb-2">{data.organization?.name}</div>
              <h1 style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }} className="mb-3">
                Multi-wave trends.
              </h1>
              <p className="lead text-base md:text-lg max-w-prose">
                Dimension-level change across the organization&rsquo;s waves. Cells
                read as the mean for that wave; the Δ columns show the signed
                shift between adjacent waves and from the earliest wave to the
                latest.
              </p>
            </header>

            {data.cohort_note && (
              <div
                className="border-l-4 pl-5 py-3 mb-10 text-sm text-gray-700 max-w-prose"
                style={{ borderColor: "#AD393E", background: "#FAF7F1" }}
                data-testid="cohort-note"
              >
                <div className="eyebrow mb-1" style={{ color: "#AD393E" }}>Composition note</div>
                {data.cohort_note}
              </div>
            )}

            <WavesTable waves={data.waves} />

            {usableWaves.length < 2 ? (
              <div className="panel mt-10" data-testid="not-enough-usable">
                <div className="eyebrow mb-2">Not enough usable waves</div>
                <p className="text-sm text-gray-700 max-w-prose">
                  At least two waves must cross the five-respondent threshold
                  before deltas can be computed. Continue collecting responses
                  in the open wave, or open the next wave when ready.
                </p>
              </div>
            ) : (
              <>
                {data.headline_delta && (
                  <DeltaTable
                    title={`Headline — wave ${data.headline_delta.from_wave} → wave ${data.headline_delta.to_wave}`}
                    eyebrow="Earliest to latest"
                    delta={data.headline_delta}
                    waveByNumber={waveByNumber}
                    findMark={findMark}
                    onMarkChange={onMarkChange}
                    testId="headline-delta-table"
                    orgId={orgId}
                  />
                )}
                {data.deltas?.map((d, i) => (
                  <DeltaTable
                    key={`${d.from_wave}-${d.to_wave}`}
                    title={`Wave ${d.from_wave} → wave ${d.to_wave}`}
                    eyebrow={`Adjacent shift ${i + 1}`}
                    delta={d}
                    waveByNumber={waveByNumber}
                    findMark={findMark}
                    onMarkChange={onMarkChange}
                    testId={`adjacent-delta-table-${d.from_wave}-${d.to_wave}`}
                    orgId={orgId}
                  />
                ))}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function WavesTable({ waves }) {
  return (
    <section className="mb-10" data-testid="waves-summary">
      <div className="eyebrow mb-3">Waves</div>
      <div className="border border-gray-100">
        <div className="grid grid-cols-[120px_1fr_120px_120px] bg-cream border-b border-gray-100">
          <Th>Wave</Th>
          <Th>Deployment</Th>
          <Th>Status</Th>
          <Th>n</Th>
        </div>
        {(waves || []).map((w) => (
          <div
            key={w.deployment_id}
            className="grid grid-cols-[120px_1fr_120px_120px] border-t border-gray-100"
            data-testid={`wave-row-${w.wave_number}`}
          >
            <Td>
              <span style={{ fontFamily: "Oswald", fontWeight: 700 }}>Wave {w.wave_number}</span>
            </Td>
            <Td>{w.deployment_name}</Td>
            <Td>
              <span
                className="text-xs uppercase tracking-widest px-3 py-1"
                style={{
                  fontFamily: "Oswald",
                  background: w.status === "open" ? "#D8E9EF" : w.status === "draft" ? "#FAF7F1" : "#EEEEEE",
                  color: w.status === "open" ? "#2F6B7C" : w.status === "draft" ? "#AD393E" : "#555555",
                }}
              >
                {w.status}
              </span>
            </Td>
            <Td>
              {w.suppressed ? (
                <span className="text-xs px-2 py-1" style={{ background: "#FAF7F1", color: "#555", fontFamily: "Oswald" }}>
                  n={w.n} · n&lt;5
                </span>
              ) : (
                <span style={{ fontFamily: "Oswald", fontWeight: 700 }}>{w.n}</span>
              )}
            </Td>
          </div>
        ))}
      </div>
    </section>
  );
}

function DeltaTable({ title, eyebrow, delta, waveByNumber, findMark, onMarkChange, testId, orgId }) {
  const toDep = Object.values(waveByNumber).find((w) => w.wave_number === delta.to_wave);
  const fromDep = Object.values(waveByNumber).find((w) => w.wave_number === delta.from_wave);

  // Sprint 9 — at most one dimension expanded per table at a time. Keeps
  // the visual model simple and avoids forking the rendering grid into
  // independent micro-states.
  const [expandedDim, setExpandedDim] = React.useState(null);

  return (
    <section className="mb-10" data-testid={testId}>
      <div className="eyebrow mb-1">{eyebrow}</div>
      <h2 className="text-lg mb-4" style={{ fontFamily: "Inter", fontWeight: 500 }}>{title}</h2>
      <div className="border border-gray-100">
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_0.8fr] bg-cream border-b border-gray-100">
          <Th>Dimension</Th>
          <Th>Wave {delta.from_wave}</Th>
          <Th>Wave {delta.to_wave}</Th>
          <Th>Δ</Th>
          <Th right>Mark</Th>
        </div>
        {DIM_ORDER.map((dimKey) => {
          const row = (delta.dimensions || []).find((d) => d.dimension === dimKey);
          if (!row) return null;
          const existing = toDep && fromDep
            ? findMark(toDep.deployment_id, fromDep.deployment_id, dimKey)
            : null;
          const isExpanded = expandedDim === dimKey;
          return (
            <React.Fragment key={dimKey}>
              <DeltaRow
                row={row}
                toDep={toDep}
                fromDep={fromDep}
                existingMark={existing}
                onMarkChange={onMarkChange}
                expanded={isExpanded}
                onToggleExpand={() => setExpandedDim(isExpanded ? null : dimKey)}
              />
              {isExpanded && (
                <ItemDrill
                  orgId={orgId}
                  dimension={dimKey}
                  fromWave={delta.from_wave}
                  toWave={delta.to_wave}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

function DeltaRow({ row, toDep, fromDep, existingMark, onMarkChange, expanded, onToggleExpand }) {
  const hasValues = row.delta_mean !== null && row.delta_mean !== undefined;
  const dCol = row.delta_mean > 0 ? "#2F6B7C" : row.delta_mean < 0 ? "#AD393E" : "#555";
  const meaningful = hasValues && Math.abs(row.delta_mean) >= 0.3;
  const canMark = hasValues && toDep && fromDep;
  // Items drill is only meaningful when there are two usable waves to
  // compare (same gate as canMark — both depend on having values on
  // both sides of the delta).
  const canDrillItems = canMark && typeof onToggleExpand === "function";

  return (
    <div
      className="grid grid-cols-[1.5fr_1fr_1fr_1fr_0.8fr] border-t border-gray-100"
      data-testid={`delta-row-${row.dimension}`}
    >
      <Td>
        <div className="flex items-start gap-2">
          {canDrillItems && (
            <button
              type="button"
              onClick={onToggleExpand}
              data-testid={`items-drill-toggle-${row.dimension}`}
              aria-expanded={expanded ? "true" : "false"}
              aria-label={`${expanded ? "Collapse" : "Expand"} item-level breakdown for ${dimensionLabel(row.dimension)}`}
              title={expanded ? "Hide items" : "Show items"}
              style={{
                fontFamily: "Oswald",
                fontSize: "12px",
                color: expanded ? "#2F6B7C" : "#555",
                background: "transparent",
                border: 0,
                padding: "2px 6px",
                cursor: "pointer",
                lineHeight: 1,
                marginTop: 2,
              }}
            >
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <div>
            <div className="text-sm" style={{ fontFamily: "Inter", fontWeight: 500 }}>
              {dimensionLabel(row.dimension)}
            </div>
            <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}>
              {dimensionShort(row.dimension)}
            </div>
          </div>
        </div>
      </Td>
      <Td>
        {hasValues ? (
          <WaveCell mean={row.from_mean} tb={row.from_tb} rubric={row.from_rubric} />
        ) : <Dash />}
      </Td>
      <Td>
        {hasValues ? (
          <WaveCell mean={row.to_mean} tb={row.to_tb} rubric={row.to_rubric} />
        ) : <Dash />}
      </Td>
      <Td>
        {hasValues ? (
          <div data-testid={`delta-value-${row.dimension}`}>
            <div style={{ fontFamily: "Oswald", color: dCol, fontWeight: meaningful ? 700 : 500, fontSize: "1.1rem" }}>
              {row.delta_mean >= 0 ? "+" : "−"}
              {Math.abs(row.delta_mean).toFixed(2)}
            </div>
            <div className="text-xs mt-0.5" style={{ fontFamily: "Oswald", color: dCol }}>
              {row.delta_tb >= 0 ? "+" : "−"}
              {Math.abs(Math.round(row.delta_tb))}% TB
            </div>
            {row.rubric_changed && (
              <div className="text-xs mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.08em", color: "#AD393E" }}>
                Rubric: {row.from_rubric} → {row.to_rubric}
              </div>
            )}
          </div>
        ) : <Dash />}
      </Td>
      <Td right>
        {canMark ? (
          <TrendMarkButton
            toDeploymentId={toDep.deployment_id}
            fromDeploymentId={fromDep.deployment_id}
            dimension={row.dimension}
            initialMark={existingMark || undefined}
            onChange={(saved) => onMarkChange(toDep.deployment_id, saved, fromDep.deployment_id, row.dimension)}
            testIdSuffix={`trend-${row.dimension}-${fromDep.wave_number}-${toDep.wave_number}`}
          />
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </Td>
    </div>
  );
}

function WaveCell({ mean, tb, rubric }) {
  const c = rubricColor(rubric);
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: "1.1rem" }}>
          {mean != null ? mean.toFixed(2) : "—"}
        </span>
        {tb != null && (
          <span className="text-xs text-gray-500">{Math.round(tb)}% TB</span>
        )}
      </div>
      {rubric && (
        <span
          className="inline-block mt-1 text-xs px-2 py-0.5"
          style={{ background: c.bg, color: c.fg, fontFamily: "Oswald", letterSpacing: "0.05em" }}
        >
          {rubric}
        </span>
      )}
    </div>
  );
}

function Dash() {
  return <span className="text-xs text-gray-400">—</span>;
}

function Th({ children, right }) {
  return (
    <div className={`px-4 py-3 ${right ? "text-right" : ""}`}>
      <div className="eyebrow text-gray-500">{children}</div>
    </div>
  );
}

function Td({ children, right }) {
  return <div className={`px-4 py-4 ${right ? "text-right" : ""}`}>{children}</div>;
}


/**
 * Sprint 9 — item-level drill for a single (dimension, wave pair).
 * Fetches /api/organizations/:orgId/trends/items?dimension=…&waves=A,B
 * the first time it opens. Sub-rows render inside the same outer
 * border as the parent DeltaTable, but with their own grid so they
 * read as a clearly indented sub-zone.
 */
function ItemDrill({ orgId, dimension, fromWave, toWave }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(
          `/api/organizations/${orgId}/trends/items`,
          { params: { dimension, waves: `${fromWave},${toWave}` } },
        );
        if (!cancelled) setItems(data.items || []);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, dimension, fromWave, toWave]);

  return (
    <div
      className="border-t border-gray-100"
      style={{ background: "#FAF7F1" }}
      data-testid={`items-drill-${dimension}`}
    >
      <div className="px-6 py-4">
        <div className="eyebrow mb-3" style={{ color: "#2F6B7C" }}>
          Item-level breakdown &middot; {dimensionShort(dimension)}
        </div>
        {loading && <div className="text-xs text-gray-500" data-testid={`items-drill-loading-${dimension}`}>Loading items…</div>}
        {error && (
          <div className="text-xs" style={{ color: "#AD393E" }} data-testid={`items-drill-error-${dimension}`}>
            {error}
          </div>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="border border-gray-100" style={{ background: "white" }}>
            <div
              className="grid bg-cream border-b border-gray-100"
              style={{ gridTemplateColumns: "auto 1fr 110px 110px 110px" }}
            >
              <Th>#</Th>
              <Th>Item</Th>
              <Th>Wave {fromWave}</Th>
              <Th>Wave {toWave}</Th>
              <Th>Δ</Th>
            </div>
            {items.map((it) => {
              const fromWaveCell = it.waves.find((w) => w.wave_number === fromWave);
              const toWaveCell = it.waves.find((w) => w.wave_number === toWave);
              const dCol = it.delta_mean > 0 ? "#2F6B7C" : it.delta_mean < 0 ? "#AD393E" : "#555";
              const meaningful = it.delta_mean !== null && Math.abs(it.delta_mean) >= 0.3;
              return (
                <div
                  key={it.item_number}
                  className="grid border-t border-gray-100"
                  style={{ gridTemplateColumns: "auto 1fr 110px 110px 110px" }}
                  data-testid={`item-drill-row-${dimension}-${it.item_number}`}
                >
                  <Td>
                    <div className="text-xs" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#555" }}>
                      {it.item_number}
                    </div>
                  </Td>
                  <Td>
                    <div className="text-sm" style={{ fontFamily: "Inter", lineHeight: 1.4 }}>
                      {it.text}
                    </div>
                    {it.is_reverse && (
                      <div className="text-xs mt-1" style={{ fontFamily: "Oswald", letterSpacing: "0.08em", color: "#AD393E" }}>
                        Reverse-scored
                      </div>
                    )}
                  </Td>
                  <Td>
                    <ItemMeanCell cell={fromWaveCell} />
                  </Td>
                  <Td>
                    <ItemMeanCell cell={toWaveCell} />
                  </Td>
                  <Td>
                    {it.delta_mean === null || it.delta_mean === undefined ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <div
                        data-testid={`item-drill-delta-${dimension}-${it.item_number}`}
                        style={{
                          fontFamily: "Oswald",
                          color: dCol,
                          fontWeight: meaningful ? 700 : 500,
                          fontSize: "1.05rem",
                        }}
                      >
                        {it.delta_mean >= 0 ? "+" : "−"}
                        {Math.abs(it.delta_mean).toFixed(2)}
                      </div>
                    )}
                  </Td>
                </div>
              );
            })}
          </div>
        )}
        <div className="text-xs text-gray-500 mt-3 max-w-prose" style={{ fontFamily: "Inter" }}>
          Means are on the 1–5 scale and already reverse-scored (so &ldquo;higher
          is stronger resilience&rdquo; in every row). Items with a Δ of
          |0.30| or more are bolded — that&rsquo;s the same threshold as
          the dimension-level table above.
        </div>
      </div>
    </div>
  );
}


function ItemMeanCell({ cell }) {
  if (!cell) return <Dash />;
  if (cell.suppressed) {
    return (
      <span
        className="text-xs px-2 py-1"
        style={{ background: "#FAF7F1", color: "#555", fontFamily: "Oswald" }}
      >
        n&lt;5
      </span>
    );
  }
  return (
    <div>
      <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: "1rem" }}>
        {cell.mean != null ? cell.mean.toFixed(2) : "—"}
      </div>
      <div className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: "Oswald" }}>
        n={cell.n}
      </div>
    </div>
  );
}
